import type {
  PlatformRole,
  PlatformUser,
  TenantRole,
} from '@sirius/contracts/admin'
import type { TFunction } from 'i18next'

/** A user's platform role by name; no role: a tenant user. */
export const roleLabel = (role: PlatformRole | null, t: TFunction) =>
  role === null
    ? t('usersPage.roles.tenantUser')
    : role === 'admin'
      ? t('usersPage.roles.platformAdmin')
      : t('usersPage.roles.technician')

/** A tenant role by name (owner, admin, member). */
export const tenantRoleLabel = (role: TenantRole, t: TFunction) =>
  t(`usersPage.tenantRoles.${role}`)

/** The value of "no platform role" in the role filter and the role picker. */
export const NO_PLATFORM_ROLE = 'none'

/** A platform role as the filter and the picker carry it. */
export const roleValue = (role: PlatformRole | null) => role ?? NO_PLATFORM_ROLE

/** The platform role a filter or picker value stands for; null: none. */
export function toPlatformRole(value: string): PlatformRole | null {
  return value === 'admin' || value === 'technician' ? value : null
}

export const statusLabel = (banned: boolean, t: TFunction) =>
  banned ? t('usersPage.status.banned') : t('usersPage.status.active')

/**
 * The options of the role filter (`platformRole`) and the role picker, the
 * highest role first. The values are `admin`, `technician` and `none`, as
 * the API's filter takes them.
 */
export const roleOptions = (t: TFunction) =>
  (['admin', 'technician', null] as const).map((role) => ({
    label: roleLabel(role, t),
    value: roleValue(role),
  }))

/** The role picker's choices: none, technician, admin. */
export const platformRoleChoices = (t: TFunction) =>
  [
    { value: NO_PLATFORM_ROLE, label: t('usersPage.roleChoices.none') },
    { value: 'technician', label: t('usersPage.roleChoices.technician') },
    { value: 'admin', label: t('usersPage.roleChoices.admin') },
  ] as const

/**
 * The options of the boolean filter (`banned`). The values are the words, as
 * the list's URL keeps them; the API takes both.
 */
export const bannedOptions = (t: TFunction) =>
  [true, false].map((banned) => ({
    label: statusLabel(banned, t),
    value: String(banned),
  }))

/** "Tenant (role), …" in the API's order (by tenant name); a dash for none. */
export function membershipsText(
  memberships: PlatformUser['memberships'],
  t: TFunction,
) {
  if (memberships.length === 0) {
    return '—'
  }
  return memberships
    .map(({ tenantName, role }) =>
      t('usersPage.membershipEntry', {
        tenant: tenantName,
        role: tenantRoleLabel(role as TenantRole, t),
      }),
    )
    .join(', ')
}
